/* Module H — predictive search drawer, wishlist (guest + account), quick view. No theme coupling: it attaches to
   the header icons it finds, and to any product card via data attributes it adds itself. ~9KB raw, <15KB budget. */
(() => {
  const cfgEl = document.getElementById('digi-h-config');
  if (!cfgEl || window.DIGI_H) return;
  const CFG = JSON.parse(cfgEl.textContent);
  const S = CFG.strings;
  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const guard = (fn) => { try { fn(); } catch (e) { (window.DIGI?.report || console.warn)('module_h', e); } };
  window.DIGI_H = true;

  /* ---------------- shared drawer ---------------- */
  let lastFocus = null;
  const drawer = document.createElement('div');
  drawer.className = 'digi-drawer';
  drawer.setAttribute('aria-hidden', 'true');
  drawer.innerHTML = `<div class="digi-drawer__overlay" data-close></div>
    <aside class="digi-drawer__panel" role="dialog" aria-modal="true" aria-labelledby="digi-drawer-title" tabindex="-1">
      <header class="digi-drawer__head"><h2 class="digi-drawer__title" id="digi-drawer-title">${esc(S.search_label)}</h2>
        <button type="button" class="digi-drawer__close" data-close aria-label="${esc(S.close)}">&times;</button></header>
      <div class="digi-drawer__extra"></div>
      <div class="digi-drawer__body"></div>
    </aside>`;
  document.body.appendChild(drawer);
  const panel = drawer.querySelector('.digi-drawer__panel');
  const titleEl = drawer.querySelector('.digi-drawer__title');
  const bodyEl = drawer.querySelector('.digi-drawer__body');
  const extraEl = drawer.querySelector('.digi-drawer__extra');
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

  const openDrawer = (title, opener) => {
    lastFocus = opener || document.activeElement;
    titleEl.textContent = title || S.search_label;   /* an empty <h2> is an a11y fault on every page */
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('digi-drawer-open');
    setTimeout(() => (extraEl.querySelector('input') || panel).focus(), 60);
  };
  const closeDrawer = () => {
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('digi-drawer-open');
    extraEl.innerHTML = '';
    lastFocus?.focus?.({ preventScroll: true });
  };
  drawer.addEventListener('click', (e) => { if (e.target.closest('[data-close]')) closeDrawer(); });
  document.addEventListener('keydown', (e) => {
    if (!drawer.classList.contains('is-open')) return;
    if (e.key === 'Escape') return closeDrawer();
    if (e.key !== 'Tab') return;
    const els = [...panel.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
    if (!els.length) return;
    const first = els[0], last = els[els.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  /* ---------------- 1. predictive search ---------------- */
  const openSearch = (opener) => guard(() => {
    openDrawer(S.search_label, opener);
    extraEl.innerHTML = `<div class="digi-search__field"><label class="digi-sr" for="digi-search-input">${esc(S.search_label)}</label>
      <input id="digi-search-input" class="digi-search__input" type="search" autocomplete="off" placeholder="${esc(S.search_placeholder)}" role="combobox" aria-expanded="false" aria-controls="digi-search-results" aria-autocomplete="list"></div>
      <p class="digi-search__status" role="status" aria-live="polite"></p>`;
    bodyEl.innerHTML = '<ul class="digi-list" id="digi-search-results" role="list"></ul>';
    const input = extraEl.querySelector('input');
    const status = extraEl.querySelector('.digi-search__status');
    const list = bodyEl.querySelector('.digi-list');
    let timer, controller;
    input.addEventListener('input', () => {
      clearTimeout(timer);
      const q = input.value.trim();
      if (q.length < 2) { list.innerHTML = ''; status.textContent = ''; input.setAttribute('aria-expanded', 'false'); return; }
      timer = setTimeout(async () => {
        controller?.abort(); controller = new AbortController();
        try {
          /* Liquid-rendered JSON view (templates/search.digi-json.liquid): works in every locale, unlike the
             predictive-search endpoint, which answers 417 "Unsupported buyer locale" on a Hebrew storefront. */
          const url = `${CFG.search_url}?q=${encodeURIComponent(q)}&type=product&options[prefix]=last&view=digi-json`;
          const res = await fetch(url, { signal: controller.signal, headers: { Accept: 'application/json' } });
          const data = await res.json();
          const items = data.products || [];
          if (!items.length) { list.innerHTML = ''; status.textContent = S.no_results.replace('{{ terms }}', q).trim() || '—'; input.setAttribute('aria-expanded', 'false'); return; }
          list.innerHTML = items.map((it) => `<li class="digi-list__item"><a href="${esc(it.url)}">
              ${it.image ? `<img class="digi-list__img" src="${esc(it.image)}" alt="" width="64" height="64" loading="lazy">` : '<span class="digi-list__img"></span>'}
              <span><span class="digi-list__title">${esc(it.title)}</span><span class="digi-list__meta">${esc(it.price || '')}</span></span></a></li>`).join('')
            + `<li class="digi-list__item"><a href="${esc(CFG.search_url)}?q=${encodeURIComponent(q)}"><span class="digi-list__img"></span><span class="digi-list__title">${esc(S.view_all)}</span></a></li>`;
          status.textContent = String(items.length);
          input.setAttribute('aria-expanded', 'true');
        } catch (e) { if (e.name !== 'AbortError') { status.textContent = S.error; window.DIGI?.report('module_h', e); } }
      }, CFG.debounce || 300);
    });
  });

  /* ---------------- 2. wishlist ---------------- */
  const KEY = 'digi_wishlist';
  const readLocal = () => { try { return JSON.parse(localStorage.getItem(KEY) || '[]'); } catch { return []; } };
  const writeLocal = (ids) => { try { localStorage.setItem(KEY, JSON.stringify(ids)); } catch {} };
  let wish = readLocal();
  if (CFG.customer && CFG.wishlist_saved) {                                   /* merge account list with guest list */
    try { const saved = JSON.parse(CFG.wishlist_saved); wish = [...new Set([...saved, ...wish])]; } catch {}
    writeLocal(wish);
  }
  /* Writing a customer metafield needs the Admin API, which a storefront cannot call without a server. Until the
     app layer exists, the list lives in localStorage (per device) and is mirrored into the cart attributes, so it
     survives a checkout on the same device and can be picked up later by the server-backed version. */
  const persist = () => {
    writeLocal(wish);
    document.querySelectorAll('[data-wish-count]').forEach((el) => { el.textContent = wish.length; el.hidden = !wish.length; });
    document.dispatchEvent(new CustomEvent('digi:wishlist-changed', { detail: { items: wish } }));
    if (CFG.customer) fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ attributes: { digi_wishlist: wish.join(',') } }) }).catch(() => {});
  };

  const toggleWish = (btn) => guard(() => {
    const id = btn.dataset.digiWish;
    const i = wish.indexOf(id);
    if (i > -1) wish.splice(i, 1); else wish.push(id);
    btn.setAttribute('aria-pressed', String(i === -1));
    btn.setAttribute('aria-label', i === -1 ? S.wishlist_remove : S.wishlist_add);
    persist();
    const live = document.getElementById('digi-live'); if (live) live.textContent = i === -1 ? S.wishlist_added : '';
  });
  const openWishlist = async (opener) => guard(async () => {
    openDrawer(S.wishlist_title, opener);
    bodyEl.innerHTML = wish.length ? '<ul class="digi-list" role="list"></ul>' : `<p class="digi-empty">${esc(S.wishlist_empty)}</p>`;
    if (!wish.length) return;
    const list = bodyEl.querySelector('.digi-list');
    for (const handle of wish) {
      try {
        const p = await (await fetch(`/products/${handle}.js`)).json();
        const li = document.createElement('li');
        li.className = 'digi-list__item';
        li.innerHTML = `<div class="digi-list__row"><a href="${esc(p.url)}" style="display:contents">
          <img class="digi-list__img" src="${esc(p.featured_image || '')}&width=128" alt="" width="64" height="64" loading="lazy">
          <span><span class="digi-list__title">${esc(p.title)}</span><span class="digi-list__meta">${(p.price / 100).toFixed(2)}</span></span></a>
          <button type="button" class="digi-list__remove" data-remove="${esc(handle)}" aria-label="${esc(S.wishlist_remove)}">&times;</button></div>`;
        list.appendChild(li);
      } catch (e) { window.DIGI?.report('module_h', e, handle); }
    }
    list.addEventListener('click', (e) => {
      const rm = e.target.closest('[data-remove]'); if (!rm) return;
      wish = wish.filter((h) => h !== rm.dataset.remove); persist();
      document.querySelectorAll(`[data-digi-wish="${rm.dataset.remove}"]`).forEach((b) => b.setAttribute('aria-pressed', 'false'));
      rm.closest('li').remove();
      if (!wish.length) bodyEl.innerHTML = `<p class="digi-empty">${esc(S.wishlist_empty)}</p>`;
    });
  });

  /* ---------------- 3. quick view ---------------- */
  const openQuickView = async (handle, opener) => guard(async () => {
    openDrawer(S.quick_view, opener);
    bodyEl.innerHTML = '<p class="digi-empty">…</p>';
    const p = await (await fetch(`/products/${handle}.js`)).json();
    const money = (c) => (c / 100).toLocaleString(undefined, { minimumFractionDigits: 2 });
    let variant = p.variants.find((v) => v.available) || p.variants[0];
    const render = () => {
      bodyEl.innerHTML = `<div class="digi-qv">
        <div class="digi-qv__media"><img src="${esc((variant.featured_image?.src || p.featured_image || '') + '&width=800')}" alt="${esc(p.title)}" loading="lazy"></div>
        <h3 class="digi-qv__title">${esc(p.title)}</h3>
        <p class="digi-qv__price">${money(variant.price)}</p>
        ${p.options.filter((o) => o.name !== 'Title').map((o, oi) => `<div class="digi-qv__options"><span class="digi-list__meta">${esc(o.name)}</span>
          <div class="digi-qv__values" role="group" aria-label="${esc(o.name)}">${o.values.map((v) => `<button type="button" class="digi-qv__value" data-opt="${oi}" data-val="${esc(v)}" aria-pressed="${variant.options[oi] === v}">${esc(v)}</button>`).join('')}</div></div>`).join('')}
        <button type="button" class="digi-qv__add"${variant.available ? '' : ' disabled'}>${esc(variant.available ? S.add_to_cart : S.sold_out)}</button>
        <a class="digi-qv__link" href="${esc(p.url)}">${esc(S.view_all)}</a>
        <p class="digi-list__meta" role="status" aria-live="polite" data-qv-status></p></div>`;
    };
    render();
    bodyEl.addEventListener('click', async (e) => {
      const val = e.target.closest('.digi-qv__value');
      if (val) {
        const opts = [...variant.options]; opts[Number(val.dataset.opt)] = val.dataset.val;
        variant = p.variants.find((v) => v.options.every((o, i) => o === opts[i])) || variant;
        render(); return;
      }
      if (!e.target.closest('.digi-qv__add')) return;
      const btn = e.target.closest('.digi-qv__add'); btn.disabled = true;
      try {
        const body = { items: [{ id: variant.id, quantity: 1 }] };
        const r = await fetch(CFG.cart_add_url + '.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
        const j = await r.json(); if (!r.ok || j.status) throw new Error(j.description || j.message);
        document.dispatchEvent(new CustomEvent('custom-cart:updated', { detail: { source: 'digi-quick-view' } }));
        closeDrawer();
        if (document.querySelector('custom-cart-drawer')) document.dispatchEvent(new CustomEvent('custom-cart:open'));
        else if (document.querySelector('cart-drawer')) document.querySelector('cart-drawer').open?.();
      } catch (err) { bodyEl.querySelector('[data-qv-status]').textContent = S.error; window.DIGI?.report('module_h', err); }
      finally { btn.disabled = false; }
    });
  });

  /* ---------------- wiring: header icons + product cards ---------------- */
  const live = document.createElement('p'); live.id = 'digi-live'; live.className = 'digi-sr'; live.setAttribute('role', 'status'); live.setAttribute('aria-live', 'polite');
  document.body.appendChild(live);

  const ICONS = {
    wish: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 20.5S3.5 14.9 3.5 9.4A4.4 4.4 0 0 1 12 7.6a4.4 4.4 0 0 1 8.5 1.8c0 5.5-8.5 11.1-8.5 11.1z"/></svg>'
  };
  const wire = () => {
    if (CFG.search) document.querySelectorAll('a[href*="/search"], .header__icon--search, details-modal .header__icon--search').forEach((el) => {
      if (el.dataset.digiSearch) return; el.dataset.digiSearch = '1';
      el.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); openSearch(el); }, true);
    });
    if (CFG.wishlist) {
      const icons = document.querySelector('.header__icons') || document.querySelector('.header__icon--cart')?.parentElement;
      if (icons && !icons.querySelector('[data-digi-wishlist-open]')) {
        const b = document.createElement('button');
        b.type = 'button'; b.className = 'digi-h-trigger'; b.dataset.digiWishlistOpen = '1';
        b.style.position = 'relative'; b.setAttribute('aria-label', S.wishlist_title);
        b.innerHTML = ICONS.wish + `<span class="digi-h-count" data-wish-count hidden>0</span>`;
        b.addEventListener('click', () => openWishlist(b));
        icons.insertBefore(b, icons.firstChild);
      }
      persist();
    }
    /* the card buttons are independent: quick view must keep working when the wishlist is off */
    if (CFG.wishlist || CFG.quick_view) {
      document.querySelectorAll('.card-wrapper, .digi-card').forEach((card) => {
        const link = card.querySelector('a[href*="/products/"]'); if (!link) return;
        const handle = decodeURIComponent(link.getAttribute('href')).split('/products/')[1]?.split(/[?#]/)[0]; if (!handle) return;
        const media = card.querySelector('.card__inner, .digi-card__media, .card__media') || card;
        media.style.position = media.style.position || 'relative';
        if (CFG.wishlist && !card.querySelector('[data-digi-wish]')) {
          const b = document.createElement('button');
          b.type = 'button'; b.className = 'digi-wish-btn'; b.dataset.digiWish = handle;
          const on = wish.includes(handle);
          b.setAttribute('aria-pressed', String(on));
          b.setAttribute('aria-label', on ? S.wishlist_remove : S.wishlist_add);
          b.innerHTML = ICONS.wish;
          b.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); toggleWish(b); });
          media.appendChild(b);
        }
        if (CFG.quick_view && !card.querySelector('.digi-qv-btn')) {
          const q = document.createElement('button');
          q.type = 'button'; q.className = 'digi-qv-btn'; q.textContent = S.quick_view;
          q.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); openQuickView(handle, q); });
          media.appendChild(q);
        }
      });
    }
  };
  wire();
  document.addEventListener('shopify:section:load', wire);
  let raf = 0;
  new MutationObserver(() => { if (raf) return; raf = requestAnimationFrame(() => { raf = 0; wire(); }); }).observe(document.body, { childList: true, subtree: true });
})();
